import { fecha, useTraduccion } from '@tallerya/i18n';
import { AvisoError, Ayuda, Boton, Chip, Menu, type ElementoMenu } from '@tallerya/ui';
import { useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { useTituloPantalla } from '../../layout/titulo';
import { useAccionDeFila } from '../../componentes/useAccionDeFila';
import { useCambiosProveedor, useFichaProveedor, version, type FichaProveedor as Ficha } from './datos';
import { EliminarProveedor } from './EliminarProveedor';
import { PanelHistorial } from './PanelHistorial';

// La ficha del proveedor (#188): consultarlo sin abrirlo como si se fuera a
// cambiar. La ganó con el historial, por la regla de `estructura-del-proyecto.md`
// §7. No hay frame en el Figma: sigue a `FichaCliente.tsx` —migas, cabecera con
// sus acciones y secciones de solo lectura—, y el Figma se ajusta después.
//
// Es la ficha más corta: el proveedor no tiene más que su código, su documento y
// su referencia, y los gastos que se le hicieron todavía no se muestran aquí.
//
// Se llega desde el listado, que manda `desdeListado` en el estado: «Atrás»
// vuelve a él con sus filtros. Quien entró por un enlace va al listado limpio.

const RUTA = '/proveedores';

export function FichaProveedor() {
  const { t } = useTraduccion();
  const navegar = useNavigate();
  const { id } = useParams();
  const consulta = useFichaProveedor(id);
  useTituloPantalla(consulta.data?.name ?? t('admin.proveedores.titulo'));

  // Al eliminar desde aquí no se llega a ver «no existe»: `eliminar` no vuelve a
  // pedir la ficha eliminada, y se sigue viendo hasta que se sale.
  const ficha = consulta.data;

  if (consulta.isPending) return <p className="entrada">{t('app.cargando')}</p>;

  if (!ficha) {
    const noExiste = consulta.data === null;
    return (
      <section className="aviso">
        <h2>{noExiste ? t('admin.proveedores.noExiste.titulo') : t('ui.listado.error.titulo')}</h2>
        <p>{noExiste ? t('admin.proveedores.noExiste.texto') : t('ui.listado.error.texto')}</p>
        <Boton variante="secundario" onClick={() => void navegar(RUTA)}>
          {t('admin.proveedores.volver')}
        </Boton>
      </section>
    );
  }

  return <Contenido ficha={ficha} />;
}

function Contenido({ ficha }: { ficha: Ficha }) {
  const { t, idioma } = useTraduccion();
  const navegar = useNavigate();
  const cambios = useCambiosProveedor();
  const { hacer, problema } = useAccionDeFila();
  const [eliminando, setEliminando] = useState(false);
  // El historial empieza cerrado y no se pide hasta abrirlo (#193).
  const [viendoHistorial, setViendoHistorial] = useState(false);
  const id = String(ficha.id);

  const desdeListado = (useLocation().state as { desdeListado?: boolean } | null)?.desdeListado ?? false;
  // Eliminado, la ficha ya no existe: se vuelve al listado que hay detrás, con
  // sus filtros, o se reemplaza esta pantalla por uno limpio.
  const alEliminar = () => void (desdeListado ? navegar(-1) : navegar(RUTA, { replace: true }));

  const nombre = { nombre: ficha.name };
  const masAcciones: ElementoMenu[] = [
    ficha.isActive
      ? {
          etiqueta: t('admin.proveedores.menu.desactivar'),
          onSeleccionar: () =>
            void hacer(
              cambios.cambiarEstado(id, version(ficha.version), false),
              t('admin.proveedores.aviso.desactivado', nombre),
            ),
        }
      : {
          etiqueta: t('admin.proveedores.menu.activar'),
          onSeleccionar: () =>
            void hacer(
              cambios.cambiarEstado(id, version(ficha.version), true),
              t('admin.proveedores.aviso.activado', nombre),
            ),
        },
    { tipo: 'divisor' },
    { etiqueta: t('admin.proveedores.menu.eliminar'), peligro: true, onSeleccionar: () => setEliminando(true) },
  ];

  // «NIT 0614-250190-102-3»: tipo y número van en pareja o no van.
  const documento = ficha.docIdentifier ? [ficha.docTypeCode, ficha.docIdentifier].filter(Boolean).join(' ') : null;

  return (
    <>
      <nav aria-label={t('admin.clientes.ficha.migas')}>
        <ol className="ficha__migas">
          <li>
            <Link to={RUTA}>{t('admin.proveedores.titulo')}</Link>
          </li>
          <li aria-current="page">{ficha.name}</li>
        </ol>
      </nav>

      {problema && <AvisoError aviso={{ titulo: t('admin.proveedores.error.titulo'), texto: problema }} />}

      <header className="tipos__cabecera">
        <div>
          <div className="tipos__titulo">
            <h1>{ficha.name}</h1>
            <Ayuda tema={ficha.name} texto={t('admin.proveedores.ficha.info')} tono="titulo" />
            {ficha.isActive ? (
              <Chip tono="exito">{t('admin.proveedores.activo')}</Chip>
            ) : (
              <Chip>{t('admin.proveedores.inactivo')}</Chip>
            )}
          </div>
          <p className="tipos__resumen">
            {[ficha.code, t('admin.proveedores.ficha.desde', { fecha: fecha(ficha.createdAt, idioma) })].join(' · ')}
          </p>
        </div>
        <div className="tipos__acciones">
          <Boton variante="secundario" onClick={() => setViendoHistorial(true)}>
            {t('admin.historial.titulo')}
          </Boton>
          <Boton
            variante="secundario"
            // `desdeFicha`: el formulario vuelve con «Atrás», que es esta ficha. Y
            // `desdeListado`, si hay un listado detrás de ella: al eliminar desde
            // la edición se salta hasta ahí.
            onClick={() => void navegar(`${RUTA}/${id}/editar`, { state: { desdeFicha: true, desdeListado } })}
          >
            {t('admin.proveedores.editar.titulo')}
          </Boton>
          <Menu
            elementos={masAcciones}
            disparador={({ alternar, atributos }) => (
              <Boton variante="secundario" soloIcono aria-label={t('admin.proveedores.mas')} onClick={alternar} {...atributos}>
                •••
              </Boton>
            )}
          />
        </div>
      </header>

      <div className="ficha__secciones">
        <section className="tipos__seccion">
          <div className="tipos__seccion-cabecera">
            <div className="titulo-con-ayuda titulo-con-ayuda--seccion">
              <h2>{t('admin.proveedores.formulario.seccion')}</h2>
              <Ayuda
                tema={t('admin.proveedores.formulario.seccion')}
                texto={t('admin.proveedores.formulario.seccionInfo')}
                tono="titulo"
              />
            </div>
          </div>
          <dl className="ficha__datos">
            <Dato etiqueta={t('admin.proveedores.formulario.codigo')}>{ficha.code}</Dato>
            <Dato etiqueta={t('admin.proveedores.columna.documento')}>
              {documento ?? t('admin.proveedores.formulario.sinDocumento')}
            </Dato>
            <Dato etiqueta={t('admin.proveedores.formulario.referencia')}>{ficha.reference ?? '—'}</Dato>
          </dl>
        </section>

        {/* Quién le cambió qué, en el panel que abre «Historial», como en la ficha del cliente (#193). */}
        <PanelHistorial proveedorId={id} abierto={viendoHistorial} onCerrar={() => setViendoHistorial(false)} />
      </div>

      <EliminarProveedor
        proveedor={eliminando ? ficha : null}
        onCerrar={() => setEliminando(false)}
        onEliminado={alEliminar}
      />
    </>
  );
}

function Dato({ etiqueta, children }: { etiqueta: string; children: ReactNode }) {
  return (
    <div className="ficha__dato">
      <dt>{etiqueta}</dt>
      <dd>{children}</dd>
    </div>
  );
}
